// Constantes de tipos de status
export const STATUS_LOADING = 'loading';
export const STATUS_SUCCESS = 'success';
export const STATUS_ERROR = 'error';
export const STATUS_SDK = 'sdk';
export const STATUS_FULLSCREEN = 'fullscreen';
export const STATUS_CLOSING = 'closing';

/**
 * Cria um objeto de status de loading
 * @param {string} text - O texto a ser exibido durante o loading
 * @returns {{type: string, text: string}} Status object
 */
export const createLoadingStatus = (text) => ({
  type: STATUS_LOADING,
  text,
});

/**
 * Cria um objeto de status de sucesso
 * @param {string} text - O texto a ser exibido
 * @returns {{type: string, text: string}} Status object
 */
export const createSuccessStatus = (text) => ({
  type: STATUS_SUCCESS,
  text,
});

/**
 * Cria um objeto de status de erro
 * @param {string} text - O texto a ser exibido
 * @returns {{type: string, text: string}} Status object
 */
export const createErrorStatus = (text) => ({
  type: STATUS_ERROR,
  text,
});

/**
 * Cria um objeto de status SDK
 * @returns {{type: string}} Status object
 */
export const createSdkStatus = () => ({
  type: STATUS_SDK,
});

/**
 * Cria um objeto de status fullscreen
 * Quando ativo, o DynamicWrapper expande para 100% do viewport sem margens nem border-radius.
 * @returns {{type: string}} Status object
 */
export const createFullscreenStatus = () => ({
  type: STATUS_FULLSCREEN,
});

/**
 * Creates a closing status object.
 * Signals SDK-S teardown: when active, the DynamicWrapper slides off screen
 * (slideOut) before the native app destroys the webview. Meant to be
 * terminal (the consumer shouldn't set another status after it), but the
 * animation hook (useSlideAnimation) handles a reversal defensively —
 * e.g. a CLOSING aborted by mistake — restoring the wrapper instead of
 * leaving it stuck off screen forever.
 * @returns {{type: string}} Status object
 */
export const createClosingStatus = () => ({
  type: STATUS_CLOSING,
});

/**
 * Verifica se há algum status ativo (qualquer tipo)
 * @param {Object|null} status - O objeto de status
 * @returns {boolean} True se há um status ativo
 */
export const hasStatus = (status) => !!status?.type;

/**
 * Verifica se o status atual é SDK
 * @param {Object|null} status - O objeto de status
 * @returns {boolean} True se é status SDK
 */
export const isSdkStatus = (status) => status?.type === STATUS_SDK;

/**
 * Verifica se o status atual é fullscreen
 * @param {Object|null} status - O objeto de status
 * @returns {boolean} True se é status fullscreen
 */
export const isFullscreenStatus = (status) => status?.type === STATUS_FULLSCREEN;

/**
 * Checks whether the current status is closing.
 * @param {Object|null} status - The status object
 * @returns {boolean} True if the status is closing
 */
export const isClosingStatus = (status) => status?.type === STATUS_CLOSING;

/**
 * Verifica se o status atual é loading
 * @param {Object|null} status - O objeto de status
 * @returns {boolean} True se está em loading
 */
export const isLoading = (status) => status?.type === STATUS_LOADING;

/**
 * Extrai o texto do status
 * @param {Object|null} status - O objeto de status
 * @returns {string} O texto do status ou string vazia
 */
export const getStatusText = (status) => status?.text || '';

/**
 * Verifica se o status é elegível para renderizar no GlobalProgress
 * (loading, success ou error — qualquer status "real" exceto SDK e fullscreen,
 * que têm apresentação própria e nunca passam pelo GlobalProgress).
 *
 * Predicado compartilhado entre ContentSwitcher (decide se renderiza o
 * GlobalProgress) e DynamicWrapper (decide quando congelar/animar a altura
 * do wrapper) — mantenha-os usando esta função em vez de reimplementar a
 * checagem em cada lugar, para que não fiquem fora de sincronia.
 * @param {Object|null} status - O objeto de status
 * @returns {boolean} True se o status deve ser exibido pelo GlobalProgress
 */
export const isGlobalProgressStatus = (status) =>
  hasStatus(status) &&
  !isSdkStatus(status) &&
  !isFullscreenStatus(status) &&
  !isClosingStatus(status);
